import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * The body of `data-screen-label="Survey results"` — shared by the Survey results screen (one campaign)
 * and the VoiceAgent page's Results tab (every conversation of the survey), so the two can never show
 * one survey two ways. AMENDED 2026-10-03 (design §16.7.2 AS BUILT 2026-10-03).
 *
 * WHAT IS ON IT, IN THE DESIGN'S ORDER: the chip row (the design's round chips, now the scope — every
 * conversation or one campaign), the filters (window, language, arm, test conversations), the
 * headline figures, the note strip (which conversations these are, always said), the full-width By
 * question / By person switch, the question cards or the respondents table, the repeat-respondents card
 * and the footnote.
 *
 * ONE SOURCE. Every figure is `getSurveyResult` / `listSurveyRespondents`, read from `survey_answer` and
 * the conversation's outcome, aggregated in SQL. The body divides nothing and fetches nothing else.
 *
 * TEST CONVERSATIONS. Left out unless the switch is on; when on, each respondent row carries a Test tag
 * and the note strip says how many are counted. The switch, like every filter, is in the URL.
 *
 * NOTHING HERE READS A CLOCK. The "as they stood at" line comes from the query's `dataUpdatedAt`.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Icon, Segmented, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Tag, classes } from "@wetalk/ui";
import type { ReactNode } from "react";

import { SwitchControl } from "../billing/switch-control.js";
import { SurveyQuestionPanel } from "./survey-question-panel.js";
import {
  NO_ANSWER_TEXT,
  RANGE_OPTION,
  RESPONDENT_FILTER,
  answerCellText,
  asAtText,
  countText,
  isSurveyResultRange,
  languageOptionText,
  questionTextOf,
  respondentRowStatusLabel,
  respondentStatusLabel,
  respondentStatusTone,
  resultFigures,
  resultLanguageOf,
  scopeNoteText,
  type RespondentFilterValue,
} from "./survey-result-copy.js";
import { GLYPH } from "../../shell/glyph.js";
import { useGateway } from "../../api/provider.js";
import { surveyRespondentQuery, surveyResultQuery } from "../../api/query.js";
import { apiErrorText } from "../../api/api-error.js";
import { languageNameOf } from "../../api/conversation-view.js";
import type { SurveyResultFilterView, SurveyResultView, SurveyVariantView } from "../../api/gateway.js";

import "../billing/billing.css";
import "./survey-result.css";

/** The two renderings the design draws: "By question" and "By person". */
export type SurveyResultViewMode = "question" | "person";

/** Everything a results page keeps in its URL. */
export interface SurveyResultSearchView {
  readonly filter: SurveyResultFilterView;
  readonly view: SurveyResultViewMode;
  readonly outcome: RespondentFilterValue;
}

export interface SurveyResultBodyProps extends SurveyResultSearchView {
  readonly voiceAgentId: string;
  /**
   * The campaign page's own campaign. Its chip row then offers that campaign and a way to every
   * conversation of the survey (the VoiceAgent's Results tab) instead of one chip per campaign.
   */
  readonly campaignScope: { readonly campaignId: string; readonly name: string } | null;
  readonly onSearch: (next: Partial<SurveyResultSearchView>) => void;
}

const VIEW_OPTION: readonly { readonly value: SurveyResultViewMode; readonly label: string }[] = [
  { value: "question", get label() { return portalText("operations", "by_question_e0b9b251"); } },
  { value: "person", get label() { return portalText("operations", "by_person_106a809c"); } },
];

const VARIANT_OPTION: readonly { readonly value: "both" | SurveyVariantView; readonly label: string }[] = [
  { value: "both", get label() { return portalText("operations", "both_arms_4451c3cc"); } },
  { value: "A", get label() { return portalText("operations", "arm_a_fbcc333a"); } },
  { value: "B", get label() { return portalText("operations", "arm_b_4b2323c0"); } },
];

export function SurveyResultBody(props: SurveyResultBodyProps): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const result = useQuery(surveyResultQuery(gateway, props.voiceAgentId, props.filter));
  const filter = props.filter;
  const setFilter = (next: Partial<SurveyResultFilterView>): void => {
    props.onSearch({ filter: { ...filter, ...next } });
  };

  return (
    <div className="srv-body">
      <ScopeChips
        voiceAgentId={props.voiceAgentId}
        campaignScope={props.campaignScope}
        result={result.data}
        filter={filter}
        onCampaign={(campaignId) => {
          setFilter({ campaignId });
        }}
      />

      <div className="c-survey-filters c-survey-result-filters">
        <label className="c-survey-select">
          <span className="c-survey-select-label">{portalText("operations", "when_769bb19e")}</span>
          <select
            className="input"
            value={filter.range}
            onChange={(event) => {
              const value = event.currentTarget.value;
              if (isSurveyResultRange(value)) setFilter({ range: value });
            }}
          >
            {RANGE_OPTION.map((one) => (
              <option key={one.value} value={one.value}>
                {one.label}
              </option>
            ))}
          </select>
        </label>
        <label className="c-survey-select">
          <span className="c-survey-select-label">{portalText("operations", "language_89b86ab0")}</span>
          <select
            className="input"
            value={filter.languageCode ?? ""}
            onChange={(event) => {
              const value = event.currentTarget.value;
              setFilter({ languageCode: value === "" ? null : value });
            }}
          >
            <option value="">{portalText("operations", "all_languages_56488c32")}</option>
            {(result.data?.language ?? []).map((one) => (
              <option key={one.languageCode} value={one.languageCode}>
                {languageOptionText(one.languageCode, one.conversationCount)}
              </option>
            ))}
            {filter.languageCode !== null && !(result.data?.language ?? []).some((one) => one.languageCode === filter.languageCode) ? (
              <option value={filter.languageCode}>{languageNameOf(filter.languageCode)}</option>
            ) : null}
          </select>
        </label>
        <Segmented
          name="surveyVariant"
          legend={portalText("operations", "questionnaire_arm_7386eb33")}
          value={filter.variant ?? "both"}
          options={[...VARIANT_OPTION]}
          onChange={(value) => {
            setFilter({ variant: value === "both" ? null : value });
          }}
        />
        <SwitchControl
          label={portalText("operations", "include_test_conversations_fd54e250")}
          checked={filter.includeIsTest}
          onChange={(includeIsTest) => {
            setFilter({ includeIsTest });
          }}
        />
      </div>

      {result.isError ? (
        <p className="c-note c-note-critical">
          {portalText("operations", "we_could_not_load_these_results_so_nothing_is_shown_rather_96b410af")}{" "}
          {apiErrorText(result.error, "")}
        </p>
      ) : result.data === undefined ? (
        <p className="c-note">{portalText("operations", "adding_up_the_answers_d0207578")}</p>
      ) : (
        <ResultBody {...props} result={result.data} dataUpdatedAt={result.dataUpdatedAt} />
      )}
    </div>
  );
}

// -------------------------------------------------------------------------------------------
// The chip row — the design's round chips, now the scope
// -------------------------------------------------------------------------------------------

function ScopeChips({
  voiceAgentId,
  campaignScope,
  result,
  filter,
  onCampaign,
}: {
  readonly voiceAgentId: string;
  readonly campaignScope: SurveyResultBodyProps["campaignScope"];
  readonly result: SurveyResultView | undefined;
  readonly filter: SurveyResultFilterView;
  readonly onCampaign: (campaignId: string | null) => void;
}): ReactNode {
  usePortalTranslation("operations");
  if (campaignScope !== null) {
    const own = result?.campaign.find((one) => one.campaignId === campaignScope.campaignId);
    return (
      <div className="c-survey-rounds" role="group" aria-label={portalText("operations", "which_conversations_50b524cd")}>
        <span className={classes("c-survey-round", "c-survey-round-on")} aria-current="true">
          {campaignScope.name}
          <span className="c-survey-round-meta">
            {own === undefined ? portalText("operations", "this_campaign_c9ae238b") : portalText("operations", "this_campaign_value_conversations_e5f9c97b", { v0: String(countText(own.conversationCount)) })}
          </span>
        </span>
        <Link
          className="c-survey-round"
          to="/agents/$voiceAgentId"
          params={{ voiceAgentId }}
          search={{ tab: "results" }}
        >
          {portalText("operations", "every_conversation_2cf0c1bb")}<span className="c-survey-round-meta">{portalText("operations", "the_survey_s_results_all_campaigns_and_tests_5a4ee0f2")}</span>
        </Link>
      </div>
    );
  }
  const campaign = result?.campaign ?? [];
  if (campaign.length === 0) return null;
  return (
    <div className="c-survey-rounds" role="group" aria-label={portalText("operations", "which_conversations_50b524cd")}>
      <button
        type="button"
        aria-pressed={filter.campaignId === null}
        className={classes("c-survey-round", filter.campaignId === null && "c-survey-round-on")}
        onClick={() => {
          onCampaign(null);
        }}
      >
        {portalText("operations", "all_conversations_6c9bd7ae")}<span className="c-survey-round-meta">{portalText("operations", "every_campaign_inbound_and_outside_a_campaign_1d40da49")}</span>
      </button>
      {campaign.map((one) => {
        const chosen = filter.campaignId === one.campaignId;
        return (
          <button
            key={one.campaignId}
            type="button"
            aria-pressed={chosen}
            className={classes("c-survey-round", chosen && "c-survey-round-on")}
            onClick={() => {
              onCampaign(one.campaignId);
            }}
          >
            {one.name}
            <span className="c-survey-round-meta">{countText(one.conversationCount)} {portalText("operations", "conversations_c5fb54c2")}</span>
          </button>
        );
      })}
    </div>
  );
}

// -------------------------------------------------------------------------------------------
// The figures, the switch and the two renderings
// -------------------------------------------------------------------------------------------

function ResultBody(
  props: SurveyResultBodyProps & { readonly result: SurveyResultView; readonly dataUpdatedAt: number },
): ReactNode {
  usePortalTranslation("operations");
  const { result } = props;
  const languageCode = resultLanguageOf(result, props.filter.languageCode);

  return (
    <>
      <div className="c-survey-figures">
        {resultFigures(result).map((one) => (
          <Figure key={one.label} value={one.value} label={one.label} />
        ))}
      </div>

      <p className="c-survey-delta">
        <Icon glyph={GLYPH.funnel} size="lg" />
        <span>
          {scopeNoteText(result, props.filter.includeIsTest)}{" "}
          {props.filter.includeIsTest || result.hiddenIsTestCount === 0 ? null : (
            <button
              type="button"
              className="c-survey-clear"
              onClick={() => {
                props.onSearch({ filter: { ...props.filter, includeIsTest: true } });
              }}
            >
              {portalText("operations", "include_them_4d937b87")}</button>
          )}
        </span>
      </p>

      {/* The design's full-width two-button switch. */}
      <div className="c-survey-views" role="group" aria-label={portalText("operations", "how_to_read_these_results_1c678f5f")}>
        {VIEW_OPTION.map((one) => (
          <button
            key={one.value}
            type="button"
            aria-pressed={props.view === one.value}
            className={classes("c-survey-view", props.view === one.value && "c-survey-view-on")}
            onClick={() => {
              props.onSearch({ view: one.value });
            }}
          >
            {one.label}
          </button>
        ))}
      </div>

      {props.view === "question" ? (
        result.question.length === 0 ? (
          <div className="c-panel c-panel-dashed">
            <p className="c-empty-title">{portalText("operations", "no_questions_to_show_6968a379")}</p>
            <p className="c-empty-dek">
              {portalText("operations", "this_survey_has_no_published_questionnaire_and_no_answers_88139d9e")}</p>
          </div>
        ) : (
          <div className="c-survey-questions">
            {result.question.map((one) => (
              <SurveyQuestionPanel key={one.questionCode} question={one} languageCode={languageCode} />
            ))}
          </div>
        )
      ) : (
        <RespondentTable {...props} languageCode={languageCode} />
      )}

      <div className="c-survey-repeat">
        <span className="c-survey-repeat-body">
          <span className="c-survey-repeat-title">{portalText("operations", "people_we_have_surveyed_more_than_once_66196e53")}</span>
          <span className="c-survey-repeat-dek">
            {portalText("operations", "each_conversation_files_its_own_answers_rather_than_editin_4b09282e")}</span>
        </span>
        <span className="c-survey-repeat-figures">
          <Figure value={countText(result.repeat.answeredTwiceCount)} label={portalText("operations", "answered_twice_413c44f5")} />
          <Figure value={countText(result.repeat.answeredThreeOrMoreCount)} label={portalText("operations", "three_or_more_9217fa1c")} />
        </span>
      </div>

      <p className="c-survey-footnote">
        {portalText("operations", "answers_are_written_down_as_the_respondent_gives_them_agai_fa2d95e2")}{languageCode === null ? portalText("operations", "the_questionnaire_s_language_2f6a8012") : languageNameOf(languageCode)}{portalText("operations", "each_conversation_s_own_answers_are_in_its_transcript_and_0fa9427c")}{asAtText(props.dataUpdatedAt)}
      </p>
    </>
  );
}

function Figure({ value, label }: { readonly value: string; readonly label: string }): ReactNode {
  usePortalTranslation("operations");
  return (
    <span className="c-survey-figure">
      <span className="c-survey-figure-value">{value}</span>
      <span className="c-survey-figure-label">{label}</span>
    </span>
  );
}

// -------------------------------------------------------------------------------------------
// By person
// -------------------------------------------------------------------------------------------

/**
 * One row per conversation, one column per questionnaire question, keyed by the question's id (the key
 * every answer is filed under) and headed by the instrument's code, with the question's text as its
 * title. The far end is masked by the API (`+30***1391`), or withheld from a member.
 */
function RespondentTable(
  props: SurveyResultBodyProps & { readonly result: SurveyResultView; readonly languageCode: string | null },
): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const page = useQuery(surveyRespondentQuery(gateway, props.voiceAgentId, props.filter, props.outcome, null));
  const question = props.result.question;
  const showCampaign = props.campaignScope === null && props.filter.campaignId === null;

  return (
    <>
      <div className="c-survey-filters">
        <Segmented
          name="surveyRespondentOutcome"
          legend={portalText("operations", "who_to_show_3ce57ef0")}
          value={props.outcome}
          options={[...RESPONDENT_FILTER]}
          onChange={(outcome) => {
            props.onSearch({ outcome });
          }}
        />
        {page.data === undefined ? null : (
          <span className="c-survey-row-note">
            {countText(Math.min(page.data.row.length, page.data.matchedCount))} {portalText("operations", "of_2449d652")}{countText(page.data.matchedCount)} {portalText("operations", "shown_552a9a57")}{page.data.nextCursor === null ? "" : portalText("operations", "the_download_has_everyone_bae1da77")}
          </span>
        )}
      </div>

      {page.isError ? (
        <p className="c-note c-note-critical">
          {portalText("operations", "these_conversations_could_not_be_listed_so_none_are_shown_22251ddc")}{apiErrorText(page.error, "")}
        </p>
      ) : page.data === undefined ? (
        <p className="c-note">{portalText("operations", "fetching_the_conversations_79fe4429")}</p>
      ) : page.data.row.length === 0 ? (
        <div className="c-panel c-panel-dashed">
          <p className="c-empty-title">{portalText("operations", "no_conversations_in_that_state_070e86e3")}</p>
          <p className="c-empty-dek">
            {props.outcome === "all"
              ? portalText("operations", "no_conversation_matches_these_filters_yet_562553f2")
              : portalText("operations", "no_conversation_here_is_value_6a974456", { v0: String(respondentStatusLabel(props.outcome).toLowerCase()) })}{" "}
            {props.outcome === "all" ? null : (
              <button
                type="button"
                className="c-survey-clear"
                onClick={() => {
                  props.onSearch({ outcome: "all" });
                }}
              >
                {portalText("operations", "show_everyone_284369fb")}</button>
            )}
          </p>
        </div>
      ) : (
        <div className="c-survey-scroll">
          <Table caption={portalText("operations", "every_conversation_in_this_view_with_its_answers_833594f7")}>
            <TableHead>
              <TableRow>
                <TableHeaderCell>{portalText("operations", "person_8c41ae88")}</TableHeaderCell>
                <TableHeaderCell>{portalText("operations", "started_faa9e7e7")}</TableHeaderCell>
                <TableHeaderCell>{portalText("operations", "status_bae7d5be")}</TableHeaderCell>
                <TableHeaderCell>{portalText("operations", "arm_e5748f57")}</TableHeaderCell>
                {showCampaign ? <TableHeaderCell>{portalText("operations", "campaign_69390e16")}</TableHeaderCell> : null}
                {question.map((one) => (
                  <TableHeaderCell key={one.questionCode}>
                    <span title={questionTextOf(one.text, props.languageCode) ?? undefined}>{one.instrumentCode}</span>
                  </TableHeaderCell>
                ))}
                <TableHeaderCell>{portalText("operations", "conversation_2a20c750")}</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {page.data.row.map((one) => (
                <TableRow key={one.conversationId}>
                  <TableCell>
                    <span className="c-survey-who">{one.partyMasked ?? portalText("operations", "number_hidden_2404466c")}</span>
                    <span className="c-survey-number">
                      {one.languageCode === null ? portalText("operations", "language_not_recorded_3f3e07f9") : languageNameOf(one.languageCode)}
                    </span>
                  </TableCell>
                  <TableCell>{one.startedText}</TableCell>
                  <TableCell>
                    <Tag status={respondentStatusTone(one.outcome)}>{respondentRowStatusLabel(one)}</Tag>
                    {one.isTest ? <Tag tone="outline">{portalText("operations", "test_640ab2ba")}</Tag> : null}
                  </TableCell>
                  <TableCell>{one.variant ?? NO_ANSWER_TEXT}</TableCell>
                  {showCampaign ? <TableCell>{one.campaignName ?? portalText("operations", "outside_a_campaign_77b13a44")}</TableCell> : null}
                  {question.map((each) => {
                    const given = one.answer.find((answer) => answer.questionCode === each.questionCode);
                    return <TableCell key={each.questionCode}>{given === undefined ? NO_ANSWER_TEXT : answerCellText(given)}</TableCell>;
                  })}
                  <TableCell>
                    <Link
                      to="/agents/$voiceAgentId"
                      params={{ voiceAgentId: props.voiceAgentId }}
                      search={{
                        tab: "history",
                        range: "all",
                        outcome: "all",
                        q: "",
                        conversation: one.conversationId,
                        analyticsRange: "14d",
                        agentVersion: "",
                        concern: "gaps",
                      }}
                    >
                      {portalText("operations", "open_cf9b7706")}</Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </>
  );
}
